import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSiteAssignment,
  createSiteChannel,
  deleteSiteAssignment,
  deleteSiteChannel,
  listSiteAssignments,
  listSiteChannels,
  searchKnownUsers,
  AdminApiError,
  type AdminSite,
  type AdminSiteAssignment,
} from '../../api/adminClient';
import { presentationFor } from '../../lib/lifecycle';
import { AlertSettingsSection } from './AlertSettingsSection';
import { LiftRulesSection } from './LiftRulesSection';

/**
 * Expanded view of one site's deployment binding, plus the two
 * lifecycle actions.
 *
 * The binding is shown READ-ONLY here and edited via the Commission
 * tab, which is the same idempotent `POST /sites/commission` call —
 * there is no separate update endpoint, and inventing an "edit" affordance
 * that silently re-runs commissioning would misrepresent what happens.
 */

function Field({ label, value, hint }: { label: string; value: string | null; hint?: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '4px 0', fontSize: 13 }}>
      <span style={{ color: 'var(--nx-muted)', minWidth: 220 }} title={hint}>
        {label}
      </span>
      {value ? (
        <code style={{ wordBreak: 'break-all' }}>{value}</code>
      ) : (
        <span style={{ color: 'var(--nx-muted)' }}>—</span>
      )}
    </div>
  );
}

/** How to address one assignee, best information first.
 *
 *  Never email alone: two distinct subs can carry one address here, so
 *  the suffix always rides along to keep two such rows distinguishable.
 *  A row with no directory entry at all is normal, not broken -- it
 *  predates the directory (created by `seed_site.py`) or belongs to
 *  someone who has not signed in since it started filling. */
function assigneeLabel(a: AdminSiteAssignment): string {
  const name = a.display_name || a.preferred_username;
  if (name && a.email) return `${name} (${a.email})`;
  if (name) return name;
  if (a.email) return a.email;
  return 'unknown user';
}

/** Turns the API's error codes into something an admin can act on.
 *
 *  Each of these is a deliberate refusal on the server, and a bare
 *  "409" would send someone looking for a bug instead of reading the
 *  reason. `unknown_principal` in particular is the expected outcome of
 *  the normal onboarding order, not a fault. */
function grantErrorMessage(err: unknown): string {
  if (err instanceof AdminApiError) {
    if (err.code === 'unknown_principal')
      return 'That person has never signed in here, so the platform does not know who they are yet. Ask them to sign in once, then assign them.';
    if (err.code === 'principal_is_platform_admin')
      return 'They are a platform admin, which already grants every site. An assignment would grant nothing and could not be revoked.';
    if (err.code === 'already_assigned') return 'They are already assigned to this site.';
    if (err.code === 'site_retired')
      return 'This site has been decommissioned. Re-commission it first — a grant made now would only take effect when someone later did, without a second look.';
    return err.message;
  }
  return 'Could not assign. The grant was not made.';
}

/**
 * A site's shared alert destinations.
 *
 * These rows have been readable and writable by the API since alerting
 * shipped, and by no user interface at all — the only way to create one
 * was an INSERT by hand, so production sites have none and every alert
 * reaches only whoever happens to have the console open.
 *
 * It is not a substitute for per-user email and does not overlap it: a
 * person's own subscription follows them and stops when they leave,
 * whereas a front-desk or engineering mailbox survives a shift handover
 * and depends on nobody having remembered to opt in.
 */
function ChannelsSection({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<'email' | 'chat'>('email');
  const [value, setValue] = useState('');
  const [severity, setSeverity] = useState<'critical' | 'warning'>('warning');
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['admin', 'channels', siteId],
    queryFn: () => listSiteChannels(siteId),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'channels', siteId] });
  };

  const create = useMutation({
    mutationFn: () =>
      createSiteChannel(siteId, {
        kind,
        config:
          kind === 'email'
            ? // Several addresses on one channel are one destination,
              // not several: they are batched into one send and share a
              // single delivery row.
              { to: value.split(',').map((a) => a.trim()).filter(Boolean) }
            : { webhook_url: value.trim() },
        min_severity: severity,
      }),
    onSuccess: () => {
      setValue('');
      setError(null);
      invalidate();
    },
    onError: (err) =>
      setError(
        err instanceof AdminApiError ? err.message : 'Could not add the destination.',
      ),
  });

  const remove = useMutation({
    mutationFn: (channelId: string) => deleteSiteChannel(siteId, channelId),
    onSuccess: invalidate,
    onError: () => setError('Could not remove it. The destination is unchanged.'),
  });

  return (
    <>
      <h4
        style={{
          marginBottom: 4,
          marginTop: 20,
          fontSize: 12,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--nx-cyan)',
        }}
      >
        Alert destinations
      </h4>

      {q.isPending && <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>Loading…</p>}

      {q.isError && (
        <p style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
          Could not load alert destinations. This is a separate request — nothing above is
          affected, and any destinations that exist are still being used.
        </p>
      )}

      {q.isSuccess && q.data.length === 0 && (
        <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>
          No shared destination. This site&apos;s alerts reach only people who have switched
          on email for themselves in their profile — nothing is sent to a mailbox that
          survives a shift change.
        </p>
      )}

      {q.isSuccess && q.data.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ color: 'var(--nx-muted)', textAlign: 'left' }}>
              <th style={{ padding: '4px 8px 4px 0', fontWeight: 400 }}>Destination</th>
              <th style={{ padding: '4px 8px', fontWeight: 400 }}>Sends</th>
              <th style={{ padding: '4px 8px', fontWeight: 400 }}>Added</th>
              <th style={{ padding: '4px 8px', fontWeight: 400 }} />
            </tr>
          </thead>
          <tbody data-testid="channels-rows">
            {q.data.map((c) => (
              <tr key={c.channel_id} style={{ borderTop: '1px solid var(--nx-border)' }}>
                <td style={{ padding: '4px 8px 4px 0' }}>
                  {c.kind === 'email'
                    ? (c.config.to ?? []).join(', ')
                    : // Masked by the server: the webhook URL is a
                      // bearer credential and is never returned.
                      `chat webhook ${c.config.webhook_url ?? ''}`}
                  {!c.enabled && (
                    <span style={{ color: 'var(--nx-muted)' }}> — disabled</span>
                  )}
                </td>
                <td style={{ padding: '4px 8px', color: 'var(--nx-muted)' }}>
                  {c.min_severity === 'critical' ? 'critical only' : 'warning and above'}
                </td>
                <td style={{ padding: '4px 8px', color: 'var(--nx-muted)' }}>
                  {new Date(c.created_at_ms).toLocaleDateString()}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                  <button
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Remove this alert destination?\n\n' +
                            'It stops receiving this site’s alerts immediately. A chat ' +
                            'webhook cannot be recovered from here — the URL is not stored ' +
                            'in readable form, so re-adding it means pasting it again.',
                        )
                      ) {
                        setError(null);
                        remove.mutate(c.channel_id);
                      }
                    }}
                    style={{ borderColor: 'var(--nx-error)', color: 'var(--nx-error)' }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {error && (
        <p
          data-testid="channel-error"
          style={{ color: 'var(--nx-error)', fontSize: 13, marginTop: 8 }}
        >
          {error}
        </p>
      )}

      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as 'email' | 'chat');
            setValue('');
            setError(null);
          }}
          data-testid="channel-kind"
        >
          <option value="email">Email</option>
          <option value="chat">Chat webhook</option>
        </select>
        <input
          type="text"
          value={value}
          placeholder={
            kind === 'email' ? 'ops@hotel.example, engineering@hotel.example' : 'https://…'
          }
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          style={{ minWidth: 300, flex: 1 }}
          data-testid="channel-value"
        />
        <select
          value={severity}
          onChange={(e) => setSeverity(e.target.value as 'critical' | 'warning')}
          data-testid="channel-severity"
        >
          <option value="warning">Warning and above</option>
          <option value="critical">Critical only</option>
        </select>
        <button
          type="button"
          disabled={!value.trim() || create.isPending}
          onClick={() => create.mutate()}
          data-testid="channel-add"
        >
          {create.isPending ? 'Adding…' : 'Add destination'}
        </button>
      </div>
      <p style={{ color: 'var(--nx-muted)', fontSize: 12, marginTop: 6 }}>
        Email destinations need a mail provider configured on the platform
        (<code>EMAIL_PROVIDER</code>/<code>EMAIL_API_KEY</code>). Without one, alerts still
        open and each send is recorded as a failed delivery.
      </p>
    </>
  );
}

function AssignmentsSection({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The by-sub escape hatch. Kept behind a disclosure that only appears
  // after a search has genuinely returned nothing, so the ordinary path
  // stays the obvious one and this is reached deliberately.
  const [bySub, setBySub] = useState(false);
  const [rawSub, setRawSub] = useState('');

  const q = useQuery({
    queryKey: ['admin', 'assignments', siteId],
    queryFn: () => listSiteAssignments(siteId),
  });

  // Only searches once the server's minimum is met, so typing one
  // character does not produce a 422 the user has to read past.
  const search = useQuery({
    queryKey: ['admin', 'users', term],
    queryFn: () => searchKnownUsers(term),
    enabled: term.trim().length >= 2,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'assignments', siteId] });
    // The site list carries assigned_user_count, which this changes.
    void qc.invalidateQueries({ queryKey: ['admin', 'sites'] });
  };

  const grant = useMutation({
    mutationFn: (v: { userSub: string; acknowledge?: boolean }) =>
      createSiteAssignment(siteId, v.userSub, v.acknowledge),
    onSuccess: () => {
      setTerm('');
      setPicked(null);
      setError(null);
      setBySub(false);
      setRawSub('');
      invalidate();
    },
    onError: (err) => setError(grantErrorMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: (assignmentId: number) => deleteSiteAssignment(siteId, assignmentId),
    onSuccess: invalidate,
    onError: () => setError('Could not revoke. Their access is unchanged.'),
  });

  return (
    <>
      <h4
        style={{
          marginBottom: 4,
          marginTop: 20,
          fontSize: 12,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--nx-cyan)',
        }}
      >
        Assigned users
      </h4>

      {q.isPending && <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>Loading…</p>}

      {q.isError && (
        <p style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
          Could not load assignments. The site list above is unaffected — this is a separate
          request.
        </p>
      )}

      {q.isSuccess && q.data.length === 0 && (
        <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>
          Nobody is assigned to this site, so no non-admin can see it.
        </p>
      )}

      {q.isSuccess && q.data.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ color: 'var(--nx-muted)', textAlign: 'left' }}>
              <th style={{ padding: '4px 8px 4px 0', fontWeight: 400 }}>User</th>
              <th style={{ padding: '4px 8px', fontWeight: 400 }}>sub…</th>
              <th style={{ padding: '4px 8px', fontWeight: 400 }}>Last seen</th>
              <th style={{ padding: '4px 8px', fontWeight: 400 }}>Granted</th>
              <th style={{ padding: '4px 8px', fontWeight: 400 }} />
            </tr>
          </thead>
          <tbody data-testid="assignments-rows">
            {q.data.map((a) => (
              <tr key={a.id} style={{ borderTop: '1px solid var(--nx-border)' }}>
                <td style={{ padding: '4px 8px 4px 0' }}>{assigneeLabel(a)}</td>
                <td style={{ padding: '4px 8px' }}>
                  <code title={a.user_sub}>…{a.sub_suffix}</code>
                </td>
                <td style={{ padding: '4px 8px', color: 'var(--nx-muted)' }}>
                  {/* "never" is ambiguous between "signed in long ago,
                      before we recorded it" and "has never authenticated
                      at all". The second is actionable -- it usually
                      means their role or project grant is wrong -- so it
                      is named. */}
                  {a.last_seen_ms ? (
                    new Date(a.last_seen_ms).toLocaleDateString()
                  ) : (
                    <em data-testid="never-signed-in" style={{ color: 'var(--nx-warning)' }}>
                      never signed in
                    </em>
                  )}
                </td>
                <td style={{ padding: '4px 8px', color: 'var(--nx-muted)' }}>
                  {a.granted_at_ms ? new Date(a.granted_at_ms).toLocaleDateString() : '—'}
                  {a.granted_by_email ? ` by ${a.granted_by_email}` : ''}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                  <button
                    type="button"
                    disabled={revoke.isPending}
                    onClick={() => {
                      // Confirmed, and the wording is specific about
                      // what revocation does NOT do: an open /live/v1
                      // stream is authorized once at connection setup
                      // and can outlive this until they reload.
                      if (
                        window.confirm(
                          `Revoke ${assigneeLabel(a)} from this site?\n\n` +
                            'Their next request will not include it. An already-open live ' +
                            'stream may persist until they reload.',
                        )
                      ) {
                        setError(null);
                        revoke.mutate(a.id);
                      }
                    }}
                    style={{ borderColor: 'var(--nx-error)', color: 'var(--nx-error)' }}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {error && (
        <p
          data-testid="assignment-error"
          style={{ color: 'var(--nx-error)', fontSize: 13, marginTop: 8 }}
        >
          {error}
        </p>
      )}

      <div style={{ marginTop: 12 }}>
        <label style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
          Assign someone
          <input
            type="search"
            value={term}
            placeholder="name or email"
            onChange={(e) => {
              setTerm(e.target.value);
              setPicked(null);
              setError(null);
            }}
            style={{ marginLeft: 8, minWidth: 240 }}
          />
        </label>

        {/* Previously unrendered: a failed search left the box looking
            like a search that found nothing, which is the state that
            sends an admin to the by-sub path for the wrong reason. */}
        {search.isError && (
          <p
            data-testid="search-error"
            style={{ color: 'var(--nx-warning)', fontSize: 13, marginTop: 6 }}
          >
            The search failed, so this is not a statement that nobody matches. Try again
            before assuming they have not signed in.
          </p>
        )}

        {search.isSuccess && term.trim().length >= 2 && search.data.length === 0 && (
          <>
            <p style={{ color: 'var(--nx-muted)', fontSize: 13, marginTop: 6 }}>
              Nobody matching has signed in here. The platform only knows people who have
              signed in at least once — it has no directory of your organisation.
            </p>
            {!bySub && (
              <button
                type="button"
                data-testid="by-sub-disclose"
                onClick={() => {
                  setBySub(true);
                  setError(null);
                }}
                style={{ marginTop: 6, fontSize: 12 }}
              >
                Assign by user ID instead
              </button>
            )}
          </>
        )}

        {bySub && (
          <div
            data-testid="by-sub-panel"
            style={{
              marginTop: 8,
              padding: 8,
              border: '1px solid var(--nx-border)',
              borderRadius: 4,
            }}
          >
            <p style={{ color: 'var(--nx-muted)', fontSize: 12, margin: '0 0 6px' }}>
              Use this only when the person cannot sign in yet — usually because their
              identity-provider role or project grant is still wrong. The platform
              <strong> cannot check that this ID exists</strong>; a wrong one creates an
              assignment that grants nothing and looks exactly like a working one.
            </p>
            <label style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
              User ID (sub)
              <input
                type="text"
                value={rawSub}
                placeholder="381885154148831291"
                onChange={(e) => {
                  setRawSub(e.target.value);
                  setError(null);
                }}
                style={{ marginLeft: 8, minWidth: 260 }}
              />
            </label>
            <button
              type="button"
              data-testid="by-sub-assign"
              disabled={!rawSub.trim() || grant.isPending}
              onClick={() => {
                const sub = rawSub.trim();
                if (
                  window.confirm(
                    `Assign user ID ${sub} to this site?\n\n` +
                      'This person has never signed in here, so the platform cannot ' +
                      'verify the ID is real. If it is wrong, the assignment will grant ' +
                      'nothing and will look correct.\n\n' +
                      'It is recorded separately in the audit trail.',
                  )
                ) {
                  setError(null);
                  grant.mutate({ userSub: sub, acknowledge: true });
                }
              }}
              style={{ marginLeft: 8 }}
            >
              {grant.isPending ? 'Assigning…' : 'Assign unverified ID'}
            </button>
          </div>
        )}

        {search.isSuccess && search.data.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0' }}>
            {search.data.map((u) => (
              <li key={u.user_sub} style={{ padding: '3px 0', fontSize: 13 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <input
                    type="radio"
                    name="assignee"
                    checked={picked === u.user_sub}
                    onChange={() => setPicked(u.user_sub)}
                  />
                  <span>
                    {u.display_name || u.preferred_username || u.email || 'unknown'}
                    {u.email ? ` (${u.email})` : ''}{' '}
                    <code style={{ color: 'var(--nx-muted)' }} title={u.user_sub}>
                      …{u.sub_suffix}
                    </code>
                    {/* Both of these change what the admin should do,
                        so they are shown inline rather than discovered
                        by having the grant rejected. */}
                    {u.sub_count_for_email > 1 && (
                      <em style={{ color: 'var(--nx-warning)' }}>
                        {' '}
                        — {u.sub_count_for_email} accounts share this address; check the
                        sub
                      </em>
                    )}
                    {u.is_platform_admin && (
                      <em style={{ color: 'var(--nx-muted)' }}>
                        {' '}
                        — platform admin, already has every site
                      </em>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          disabled={!picked || grant.isPending}
          onClick={() => {
            setError(null);
            if (picked) grant.mutate({ userSub: picked });
          }}
          style={{ marginTop: 8 }}
        >
          {grant.isPending ? 'Assigning…' : 'Assign to this site'}
        </button>
      </div>

      <p style={{ color: 'var(--nx-muted)', fontSize: 12, marginTop: 8 }}>
        This is not everyone who can see the site. <code>platform_admin</code> bypasses
        assignment entirely, so admins never appear here and cannot be revoked here.
      </p>
      <p style={{ color: 'var(--nx-muted)', fontSize: 12 }}>
        Revoking takes effect on their next request. An already-open live stream is authorised
        once at connection setup and may persist until they reload.
      </p>
    </>
  );
}

export function SiteDetail({
  site,
  onDecommission,
}: {
  site: AdminSite;
  onDecommission: () => void;
}) {
  const p = presentationFor(site.lifecycle_state);
  const b = site.binding;

  return (
    <section
      data-testid="site-detail"
      style={{
        marginTop: 20,
        border: '1px solid var(--nx-border)',
        borderRadius: 'var(--nx-r)',
        background: 'var(--nx-bg2)',
        padding: 20,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0 }}>{site.label}</h3>
          <code style={{ color: 'var(--nx-muted)' }}>{site.site_id}</code>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Link to={`/commission?site=${encodeURIComponent(site.site_id)}`}>
            <button type="button">Edit binding</button>
          </Link>
          <button
            type="button"
            onClick={onDecommission}
            style={{ borderColor: 'var(--nx-error)', color: 'var(--nx-error)' }}
          >
            {site.archived ? 'Re-run decommission' : 'Decommission…'}
          </button>
        </div>
      </div>

      <p
        style={{
          marginTop: 12,
          padding: '10px 12px',
          borderRadius: 'var(--nx-r)',
          border: `1px solid ${p.needsAttention ? 'var(--nx-warning)' : 'var(--nx-border)'}`,
          fontSize: 13,
        }}
      >
        <strong>{p.label}</strong> — {p.explanation}
      </p>

      {site.archived && (
        <p style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
          Archived {site.archived_at_ms ? new Date(site.archived_at_ms).toLocaleString() : ''}.
          Re-commissioning this site from the Commission tab un-archives it — that is the supported
          way to bring a decommissioned site back.
        </p>
      )}

      <div style={{ marginTop: 12 }}>
        <Field label="Timezone" value={site.timezone} />
        <Field
          label="Assigned users"
          value={String(site.assigned_user_count)}
          hint="UserSiteAssignment rows. Per-user authorization stays at the edge; the bus ACL scopes sites, not users."
        />
        <Field
          label="Last heartbeat"
          value={site.last_heartbeat_ms ? new Date(site.last_heartbeat_ms).toLocaleString() : null}
          hint="The worker's own clock, as published. Staleness is judged on local receipt time, not this value."
        />
      </div>

      <AssignmentsSection siteId={site.site_id} />

      <ChannelsSection siteId={site.site_id} />

      <AlertSettingsSection siteId={site.site_id} archived={site.archived} />

      <LiftRulesSection siteId={site.site_id} archived={site.archived} />

      <h4 style={{ marginBottom: 4, marginTop: 20, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--nx-cyan)' }}>
        Deployment binding
      </h4>
      {b === null ? (
        <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>
          No deployment binding. This site exists as a record only.
        </p>
      ) : (
        <div>
          <Field label="Platform bus endpoint" value={b.platform_bus_endpoint} hint="Plane B — the federated bus this site publishes its canonical keyspace to." />
          <Field label="Site router endpoint" value={b.zenoh_router_endpoint} hint="Plane A — site-local ROS/RMW. Must never federate; only the worker bridges the two." />
          <Field label="RMF session URI" value={b.rmf_session_uri} />
          <Field label="Command key prefix" value={b.site_worker_command_keyexpr_prefix} />
          <Field label="Broker endpoint" value={b.broker_endpoint} />
          <Field
            label="Broker token ref"
            value={b.broker_token_ref}
            hint="An env var NAME or secret path — never the token itself."
          />
          <Field label="Manifest version" value={b.deployment_manifest_version} />
          <Field label="Worker image digest" value={b.worker_image_digest} />
          <Field
            label="Certified capabilities"
            value={b.certified_capabilities.length > 0 ? b.certified_capabilities.join(', ') : null}
          />
        </div>
      )}
    </section>
  );
}
